import { useForm } from "react-hook-form";
import {
  Button,
  CloseButton,
  FormGroup,
  Input,
  Label,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  StatusSelect,
  UploadBox,
  UploadInput,
  UploadPreview,
} from "../../../style/admin/BlogCreateModal_Style";
import QuillEditor from "../../../components/QuillEditor";
import { Box } from "@mui/material";
import { EBlogResponse } from "../../../models/blog";
import { useEffect, useRef, useState } from "react";
import UploadFileService from "../../../services/api/uploadFile.service";
import { toast } from "../../../utils/notify";
import BlogService from "../../../services/api/blog.service";
import { jwtDecode } from "jwt-decode";
import { ITokenPayload } from "../../../models/user";
import { envConfig } from "../../../config/env.config";
import { IBaseResponseModel } from "../../../models/common/models.type";
import { EContentResponse } from "../../../models/seo";
import { DocumentNoteHeader } from "../../../AdminLayout_Style";
import { useNavigate } from "react-router-dom";
import { forwardRef, useImperativeHandle } from "react";
import TiptapEditor from "../../../components/TipTapEditor";

interface BlogDetailProps {
  blog: EBlogResponse | null;
  objId: string | null;
}
interface BlogDetailRef {
  validate: () => Promise<boolean>;
  submit: () => Promise<void>;
}

const BlogDetail = forwardRef<BlogDetailRef, BlogDetailProps>(
  ({ blog, objId }, ref) => {
    const {
      register,
      setValue,
      watch,
      reset,
      trigger,
      handleSubmit,

      formState: { errors },
    } = useForm<EBlogResponse>({
      defaultValues: {
        title: "",
        slug: "",
        content: "",
        status: false,
        thumbnail: null,
        avatar: null,
        authorName: null,
        language: envConfig.KEY_BASE === "vn" ? "VI" : "EN",
      },
    });
    const isFirstLoad = useRef(true);
    const [preview, setPreview] = useState<string | null>(null);
    const [user, setUser] = useState<ITokenPayload | null>();
    const title = watch("title");
    const navigate = useNavigate();
    const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

    const thumbnail = watch("thumbnail");
    const avatar = watch("avatar");

    const content = watch("content");

    useEffect(() => {
      const token = localStorage.getItem("token");

      if (token) {
        const user = jwtDecode<ITokenPayload>(token);
        user && setUser(user);
      }
    }, []);

    useEffect(() => {
      if (blog) {
        setValue("id", blog.id);
        setValue("content", blog.content);
        setValue("dateTime", blog.dateTime);
        setValue("description", blog.description);
        setValue("image", blog.image);
        setValue("slug", blog.slug);
        setValue("status", blog.status);
        setValue("title", blog.title);
        setValue("avatar", blog.avatar);
        setValue("authorName", blog.authorName);
        setValue("language", blog.language ?? "EN");

        const url = envConfig.MEDIA_ENDPOINT + blog.image;
        const urlAvatar = envConfig.MEDIA_ENDPOINT + blog.avatar;
        setAvatarPreview(urlAvatar ?? null);
        setPreview(url ?? null);
      }
    }, [blog]);

    useEffect(() => {
      if (!title) return;

      // Bỏ qua lần set title từ props
      if (isFirstLoad.current) {
        isFirstLoad.current = false;
        return;
      }

      const slug = slugify(title);
      setValue("slug", slug, { shouldValidate: true });
    }, [title]);

    useEffect(() => {
      const file = watch("thumbnail");

      if (file && file instanceof File) {
        const url = URL.createObjectURL(file);
        setPreview(url);

        return () => URL.revokeObjectURL(url);
      }
    }, [watch("thumbnail")]);

    useEffect(() => {
      const fileAvatar = watch("avatar");

      if (fileAvatar && fileAvatar instanceof File) {
        const url = URL.createObjectURL(fileAvatar);

        setAvatarPreview(url);

        return () => URL.revokeObjectURL(url);
      }
    }, [watch("avatar")]);

    const onSubmit = async (data: EBlogResponse) => {
      let path = "";
      let avatarPath = "";

      const formDataUploadFile = new FormData();

      formDataUploadFile.append("FolderName", "DocumentTraslate_Blog");

      const hasThumbnailFile = data.thumbnail instanceof File;
      const hasAvatarFile = data.avatar instanceof File;

      // append theo thứ tự
      if (hasThumbnailFile) {
        data.thumbnail && formDataUploadFile.append("Files", data.thumbnail);
      }

      if (hasAvatarFile) {
        data.avatar && formDataUploadFile.append("Files", data.avatar);
      }

      if (hasThumbnailFile || hasAvatarFile) {
        const response = await UploadFileService.uploadFile(formDataUploadFile);

        if (response.success) {
          let index = 0;

          // file đầu tiên = thumbnail
          if (hasThumbnailFile) {
            path = response.data[index]?.path ?? "";
            index++;
          }

          // file thứ hai = avatar
          if (hasAvatarFile) {
            avatarPath = response.data[index]?.path ?? "";
          }
        } else {
          toast(response?.error ?? "", "error");
          return;
        }
      }

      const formData = new FormData();

      formData.append("title", data.title);
      formData.append("slug", data.slug);
      formData.append("description", data.description);
      formData.append("content", data.content);
      formData.append("status", String(data.status));
      formData.append("image", path ? path : data.image ? data.image : "");
      formData.append("avatar", avatarPath ? avatarPath : (data?.avatar ?? ""));
      formData.append("authorName", data.authorName ?? "");
      formData.append("language", data.language ?? "EN");

      // call api
      let res: IBaseResponseModel<EContentResponse>;
      if (blog) {
        data.id && formData.append("id", data.id);
        res = await BlogService.update(formData);
      } else {
        objId && formData.append("id", objId);
        res = await BlogService.create(formData);
      }

      if (res.success) {
        setValue("thumbnail", null);
        reset();
      } else {
        toast(res.message ?? "", "error");
      }
    };

    const submitForm = handleSubmit(onSubmit);

    useImperativeHandle(ref, () => ({
      submit: submitForm,
      validate: async () => {
        const isValid = await trigger();

        if (!isValid) {
          toast("Vui lòng kiểm tra lại dữ liệu blog", "info");
        }

        return isValid;
      },
    }));

    const slugify = (text: string) => {
      return text
        .toLowerCase()
        .normalize("NFD") // tách dấu tiếng Việt
        .replace(/[\u0300-\u036f]/g, "") // xóa dấu
        .replace(/đ/g, "d")
        .replace(/[^a-z0-9\s-]/g, "") // xóa ký tự đặc biệt
        .trim()
        .replace(/\s+/g, "-") // space → -
        .replace(/-+/g, "-"); // nhiều - → 1 -
    };

    return (
      <>
        <DocumentNoteHeader>
          <h3>Nhập blog</h3>
          {/* <button onClick={() => handleOpenModal()}>+ Thêm Blog</button> */}
        </DocumentNoteHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Box>
            {envConfig.KEY_BASE === "vn" ? (
              <FormGroup>
                <Label>Ngôn ngữ</Label>

                <StatusSelect {...register("language")}>
                  <option value="VI">🇻🇳 Tiếng Việt</option>
                  <option value="EN">🇬🇧 English</option>
                </StatusSelect>
              </FormGroup>
            ) : (
              <FormGroup>
                <Label>Ngôn ngữ</Label>

                <StatusSelect {...register("language")}>
                  <option value="EN">🇬🇧 English</option>
                  {/* <option value="VI">🇻🇳 Tiếng Việt</option> */}
                  <option value="ID">🇮🇩 Indonesia</option>
                  <option value="TH">🇹🇭 Thailand</option>
                </StatusSelect>
              </FormGroup>
            )}

            <FormGroup>
              <Label>Ảnh đại diện</Label>

              <UploadInput
                id="thumbnail"
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setValue("thumbnail", file);
                  }
                }}
              />

              <UploadBox htmlFor="thumbnail">
                {preview ? (
                  <>
                    <UploadPreview src={preview} alt="thumbnail" />

                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        setValue("thumbnail", null);
                        setPreview(null);
                      }}
                      style={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        border: "none",
                        background: "rgba(0,0,0,0.7)",
                        color: "#fff",
                        cursor: "pointer",
                        fontSize: 16,
                        fontWeight: 700,
                      }}>
                      ×
                    </button>
                  </>
                ) : (
                  <>
                    <strong>📷 Chọn ảnh</strong>
                    <span>PNG, JPG, WEBP</span>
                  </>
                )}
              </UploadBox>
            </FormGroup>

            <FormGroup>
              <Label>Avatar</Label>

              <UploadInput
                id="avatar"
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];

                  if (file) {
                    setValue("avatar", file);
                  }
                }}
              />

              <UploadBox htmlFor="avatar">
                {avatarPreview ? (
                  <>
                    <UploadPreview src={avatarPreview} alt="avatar" />

                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        setValue("avatar", null);
                        setAvatarPreview(null);
                      }}
                      style={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        border: "none",
                        background: "rgba(0,0,0,0.7)",
                        color: "#fff",
                        cursor: "pointer",
                        fontSize: 16,
                        fontWeight: 700,
                      }}>
                      ×
                    </button>
                  </>
                ) : (
                  <>
                    <strong>👤 Chọn avatar</strong>
                    <span>PNG, JPG, WEBP</span>
                  </>
                )}
              </UploadBox>
            </FormGroup>

            <FormGroup>
              <Label>Tác giả</Label>
              <Input
                {...register("authorName", {
                  required: "Tiêu đề không được để trống",
                })}
              />
            </FormGroup>

            <FormGroup>
              <Label>Tiêu đề</Label>
              <Input
                {...register("title", {
                  required: "Tiêu đề không được để trống",
                })}
              />
              {errors.title && (
                <span style={{ color: "#e53935", fontSize: 13 }}>
                  {errors.title.message}
                </span>
              )}
            </FormGroup>

            <FormGroup>
              <Label>Slug</Label>
              <Input
                {...register("slug", {
                  required: "Slug không được để trống",
                })}
              />
              {errors.slug && (
                <span style={{ color: "#e53935", fontSize: 13 }}>
                  {errors.slug.message}
                </span>
              )}
            </FormGroup>

            <FormGroup>
              <Label>Mô tả</Label>
              <textarea
                style={{ height: "100px" }}
                {...register("description", {
                  required: "Mô tả không được để trống",
                })}
              />
              {errors.description && (
                <span style={{ color: "#e53935", fontSize: 13 }}>
                  {errors.description.message}
                </span>
              )}
            </FormGroup>

            <FormGroup>
              <Label>Nội dung</Label>

              <TiptapEditor
                value={content}
                onChange={(val) =>
                  setValue("content", val, { shouldValidate: true })
                }
                height={700}></TiptapEditor>

              {/* <QuillEditor
                height={700}
                value={content}
                onChange={(val) =>
                  setValue("content", val, { shouldValidate: true })
                }
              /> */}
              {errors.content && (
                <span style={{ color: "#e53935", fontSize: 13 }}>
                  Nội dung không được để trống
                </span>
              )}
            </FormGroup>

            <FormGroup>
              <Label>Trạng thái</Label>
              <StatusSelect {...register("status")}>
                <option value="true">🟢 Hiển thị</option>
                <option value="false">⚪ Ẩn</option>
              </StatusSelect>
            </FormGroup>
          </Box>
        </form>
      </>
    );
  },
);

export default BlogDetail;
